import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  CardActions,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppTheme from "../theme/AppTheme";
// import CurrentOrder from "../pages/CurrentOrder";
import CustomerOrderList from "./CustomerOrderList";
import { useAppSelector } from "../hooks/redux-hooks";
import { SERVICE_TYPE, customerStatus } from "../constants";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`orders-tabpanel-${index}`}
      aria-labelledby={`orders-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `orders-tab-${index}`,
    "aria-controls": `orders-tabpanel-${index}`,
  };
}

const OrdersPage = (props: any) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [savedOrder, setSavedOrder] = useState<any>(null);
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);

  useEffect(() => {
    // 读取本地存储的未完成订单
    const savedOrderStr = localStorage.getItem("savedOrder");
    // console.log("savedOrderStr1:", savedOrderStr);
    // console.log("basicUserInfo:", basicUserInfo);
    if (savedOrderStr) {
      try {
        const savedOrderData = JSON.parse(savedOrderStr);
        // 检查订单是否属于当前登录用户
        if (savedOrderData.uid === basicUserInfo?.uid) {
          // console.log("savedOrderData2:", savedOrderData);
          delete savedOrderData.uid;
          //delete savedOrderData.savedAt;
          delete savedOrderData.id;
          setSavedOrder(savedOrderData);
        }
      } catch (error) {
        console.error("解析保存的订单数据失败:", error);
      }
    }
  }, [basicUserInfo]);

  if (!basicUserInfo || basicUserInfo.status === customerStatus.BAN) {
    return <Navigate replace to={"/login"} />;
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleBack = () => {
    navigate('/select');
  };

  const handleContinueOrder = () => {
    if (savedOrder) {
      // 处理数据，确保布尔值字段正确转换
      const processedOrder = {
        ...savedOrder,
        // 转换可能存在的布尔值字段
        is_luggage:
          savedOrder.is_luggage === "true"
            ? true
            : savedOrder.is_luggage === "false"
            ? false
            : Boolean(savedOrder.is_luggage),
        is_child_seat:
          savedOrder.is_child_seat === "true"
            ? true
            : savedOrder.is_child_seat === "false"
            ? false
            : Boolean(savedOrder.is_child_seat),
        // 其他可能需要转换的布尔值字段
        // ...
      };
      if (savedOrder.service_type === SERVICE_TYPE.AIRPORT_PICK_UP) {
        navigate("/pickup", { state: { orderInfo: processedOrder, fromSavedOrder: true } });
      } else if (savedOrder.service_type === SERVICE_TYPE.AIRPORT_DROP_OFF) {
        navigate("/dropoff", { state: { orderInfo: processedOrder, fromSavedOrder: true } });
      }
    }
  };

  const handleDeleteSavedOrder = () => {
    localStorage.removeItem("savedOrder");
    setSavedOrder(null);
  };

  return (
    <AppTheme {...props}>
      <Box sx={{ flexGrow: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            aria-label="back"
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="orders tabs"
            variant="standard"
            sx={{ flexGrow: 1 }}
            TabIndicatorProps={{
              style: {
                backgroundColor: "#1976d2", // 更鲜明的蓝色
                height: 3, // 加粗指示器
              },
            }}
            textColor="primary"
          >
            <Tab
              label={t("currentOrders")}
              {...a11yProps(0)}
              sx={{
                fontWeight: value === 0 ? "bold" : "normal",
                color: value === 0 ? "#1976d2" : "text.primary",
              }}
            />
            <Tab
              label={t("savedOrders")}
              {...a11yProps(1)}
              sx={{
                fontWeight: value === 1 ? "bold" : "normal",
                color: value === 1 ? "#1976d2" : "text.primary",
              }}
            />
          </Tabs>
        </Box>

        <TabPanel value={value} index={0}>
          {/* <CurrentOrder /> */}
          <CustomerOrderList />
        </TabPanel>

        <TabPanel value={value} index={1}>
          {savedOrder ? (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {savedOrder.service_type === SERVICE_TYPE.AIRPORT_PICK_UP
                    ? t("pickupService")
                    : t("dropoffService")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("savedAt")}:{" "}
                  {new Date(savedOrder.savedAt).toLocaleString()}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1">
                  {t("name")}: {savedOrder.customer_name}
                </Typography>
                {savedOrder.service_type === SERVICE_TYPE.AIRPORT_PICK_UP ? (
                  <>
                    <Typography variant="body1">
                      {t("pickupAirport")}: {savedOrder.airport}
                    </Typography>
                    <Typography variant="body1">
                      {t("dropOffLocation")}: {savedOrder.addr1}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1">
                      {t("pickupLocation")}: {savedOrder.addr1}
                    </Typography>
                    <Typography variant="body1">
                      {t("dropOffAirport")}: {savedOrder.airport}
                    </Typography>
                  </>
                )}
              </CardContent>
              <CardActions>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleContinueOrder}
                >
                  {t("continueOrder")}
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={handleDeleteSavedOrder}
                >
                  {t("deleteOrder")}
                </Button>
              </CardActions>
            </Card>
          ) : (
            <Typography variant="body1" align="center" sx={{ mt: 4 }}>
              {t("noSavedOrders")}
            </Typography>
          )}
        </TabPanel>
      </Box>
    </AppTheme>
  );
};

export default OrdersPage;
